import { useUser } from '@stackframe/react';
import { Building2, ChevronDown, PlusCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getActiveStackTeamId, setActiveStackTeamId } from '../lib/activeTenant.ts';

interface Props {
  activeStackTeamId: string | null;
  onChange: (id: string | null) => void;
}

interface TeamLike {
  id: string;
  displayName: string;
}

export function TenantSwitcher({ activeStackTeamId, onChange }: Props) {
  const user = useUser();
  // useTeams() exists on the user instance in @stackframe/react. The bare
  // `useTeams` export was removed in 2.7+; we go through the user.
  const teams: TeamLike[] = (user?.useTeams() ?? []) as TeamLike[];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Auto-pick first team if none selected.
  useEffect(() => {
    if (!activeStackTeamId && teams.length > 0) {
      const stored = getActiveStackTeamId();
      const exists = stored ? teams.find((t) => t.id === stored) : undefined;
      const next = exists?.id ?? teams[0]!.id;
      onChange(next);
      setActiveStackTeamId(next);
    }
  }, [activeStackTeamId, teams, onChange]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const active = teams.find((t) => t.id === activeStackTeamId) ?? null;

  if (!user) return null;

  async function createTeam() {
    if (!user) return;
    const name = window.prompt('Name for the new tenant?');
    if (!name) return;
    const team = await user.createTeam({ displayName: name });
    onChange(team.id);
    setActiveStackTeamId(team.id);
    setOpen(false);
  }

  return (
    <div className="tenant-switcher" ref={ref} style={{ position: 'relative' }}>
      <button
        className="secondary"
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', justifyContent: 'flex-start' }}
      >
        <Building2 size={14} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {active?.displayName ?? 'No tenant'}
        </span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            padding: 4,
          }}
        >
          {teams.map((t) => (
            <button
              key={t.id}
              className="ghost"
              style={{ width: '100%', justifyContent: 'flex-start' }}
              onClick={() => {
                onChange(t.id);
                setActiveStackTeamId(t.id);
                setOpen(false);
              }}
            >
              <Building2 size={14} />
              {t.displayName}
              {t.id === activeStackTeamId && (
                <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
                  active
                </span>
              )}
            </button>
          ))}
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <button
            className="ghost"
            style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={() => void createTeam()}
          >
            <PlusCircle size={14} />
            New tenant
          </button>
        </div>
      )}
    </div>
  );
}
